/**
 * SchoolLMS — Full LMS: Lessons, Assignments, Quizzes, Live Classes, Catch-up
 * Enhanced with edit capabilities, publish toggles, status management, attempt results
 */
import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import ReopenDialog from '@/components/school/ReopenDialog';
import WorkflowChecklist from '@/components/workflow/WorkflowChecklist';
import { toast } from 'sonner';
import {
  Plus, Loader2, BookOpen, FileText, ClipboardList, Video, Play,
  Trash2, CheckCircle, Clock, Award, Search, Edit2, ArrowLeft,
  GraduationCap, Tag, Eye, Radio, XCircle, Link2,
  Sparkles, CalendarDays, BarChart3, Settings2, AlertTriangle, UserCheck,
  BookMarked, TrendingUp, CheckSquare
} from 'lucide-react';
import { LessonPlansTab, SyllabusProgressTab } from '@/components/school/LMSPhaseCPanels';
import { getToken } from '@/lib/authToken';
import { MediaUpload } from '@/components/MediaUpload';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const api = async (path, opts = {}) => {
  const res = await fetch(`${API_URL}/api/school${path}`, { ...opts, headers: { 'Authorization': `Bearer ${getToken()}`, 'Content-Type': 'application/json', ...opts.headers } });
  if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.detail || 'Request failed'); }
  return res.json();
};

const StatCard = ({ label, value, sub, icon: Icon, color }) => (
  <Card>
    <CardContent className="p-4 text-center">
      <Icon className={`h-5 w-5 mx-auto mb-1 text-${color}-500`} />
      <p className="text-2xl font-bold text-slate-900">{value}</p>
      <p className="text-xs text-slate-500">{label}</p>
      <p className="text-[10px] text-slate-400 mt-0.5">{sub}</p>
    </CardContent>
  </Card>
);

// ==================== LMS DASHBOARD ====================
const LMSDashboard = ({ tenantId }) => {
  const { data: stats, isLoading } = useQuery({
    queryKey: ['lms-dashboard', tenantId],
    queryFn: () => api(`/tenants/${tenantId}/lms/dashboard`),
  });
  if (isLoading) return <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  const cards = [
    { label: 'Lessons', value: stats?.lessons || 0, sub: `${stats?.published_lessons || 0} published`, icon: BookOpen, color: 'indigo' },
    { label: 'Assignments', value: stats?.assignments || 0, sub: `${stats?.pending_submissions || 0} pending`, icon: ClipboardList, color: 'emerald' },
    { label: 'Quizzes', value: stats?.quizzes || 0, sub: `${stats?.quiz_attempts || 0} attempts`, icon: Award, color: 'amber' },
    { label: 'Live Classes', value: stats?.live_classes || 0, sub: `${stats?.upcoming_classes || 0} upcoming`, icon: Video, color: 'rose' },
    { label: 'Catch-up', value: stats?.catchup_content || 0, sub: 'resources', icon: Play, color: 'sky' },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="lms-dashboard">
      {cards.map(c => <StatCard key={c.label} {...c} />)}
    </div>
  );
};

// =============== TRANSCRIPTS ATTACHED TO A LESSON ===============
// Shows transcripts already linked to this lesson (link_module='lms_lesson',
// link_id=lesson.id), lets a teacher attach an existing transcription job, OR
// upload a new recording inline. Students see this same panel (read-only) on
// their lesson view — captions on the video, full transcript searchable.
const LessonTranscriptsPanel = ({ tenantId, lessonId }) => {
  const qc = useQueryClient();
  const [showAttachAll, setShowAttachAll] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadTitle, setUploadTitle] = useState('');

  const apiUrl = process.env.REACT_APP_BACKEND_URL;
  const authHeader = { 'Authorization': `Bearer ${getToken()}` };

  // Transcripts already attached.
  const { data: attached, isLoading } = useQuery({
    queryKey: ['lesson-transcripts', lessonId],
    queryFn: async () => {
      const r = await fetch(`${apiUrl}/api/transcription/jobs?link_module=lms_lesson&link_id=${lessonId}`, { headers: authHeader });
      return r.ok ? r.json() : { jobs: [] };
    },
  });

  // Catalog of every job for the "Attach existing" dropdown.
  const { data: allJobs } = useQuery({
    queryKey: ['transcription-jobs-all'],
    queryFn: async () => {
      const r = await fetch(`${apiUrl}/api/transcription/jobs?limit=100`, { headers: authHeader });
      return r.ok ? r.json() : { jobs: [] };
    },
    enabled: showAttachAll,
  });

  const attachExisting = useMutation({
    mutationFn: async (jobId) => {
      const r = await fetch(`${apiUrl}/api/transcription/jobs/${jobId}`, {
        method: 'PUT',
        headers: { ...authHeader, 'Content-Type': 'application/json' },
        // Reuse the existing PUT endpoint by setting link_module/link_id via segments=null;
        // backend ignores null segments. We patch the link fields through a tiny dedicated route below.
        body: JSON.stringify({}),
      });
      // Then PATCH the link fields via a small inline endpoint we add (see backend).
      await fetch(`${apiUrl}/api/transcription/jobs/${jobId}/attach?link_module=lms_lesson&link_id=${lessonId}`, {
        method: 'POST', headers: authHeader,
      });
      return r.ok;
    },
    onSuccess: () => { toast.success('Transcript attached'); setShowAttachAll(false); qc.invalidateQueries({ queryKey: ['lesson-transcripts', lessonId] }); },
    onError: e => toast.error(e.message),
  });

  const uploadAndAttach = useMutation({
    mutationFn: async () => {
      if (!uploadFile) return;
      const fd = new FormData();
      fd.append('file', uploadFile);
      if (uploadTitle) fd.append('title', uploadTitle);
      fd.append('link_module', 'lms_lesson');
      fd.append('link_id', lessonId);
      const r = await fetch(`${apiUrl}/api/transcription/jobs`, { method: 'POST', headers: authHeader, body: fd });
      if (!r.ok) throw new Error('Upload failed');
      return r.json();
    },
    onSuccess: () => { toast.success('Queued — transcript will appear once processed'); setUploadFile(null); setUploadTitle(''); qc.invalidateQueries({ queryKey: ['lesson-transcripts', lessonId] }); },
    onError: e => toast.error(e.message),
  });

  const summarise = useMutation({
    mutationFn: (jobId) => fetch(`${apiUrl}/api/transcription/jobs/${jobId}/summarize`, { method: 'POST', headers: authHeader }).then(r => r.ok ? r.json() : Promise.reject(new Error('Summary failed'))),
    onSuccess: () => { toast.success('AI summary generated'); qc.invalidateQueries({ queryKey: ['lesson-transcripts', lessonId] }); },
    onError: e => toast.error(e.message),
  });

  const detach = useMutation({
    mutationFn: (jobId) => fetch(`${apiUrl}/api/transcription/jobs/${jobId}/attach`, { method: 'DELETE', headers: authHeader }),
    onSuccess: () => { toast.success('Detached'); qc.invalidateQueries({ queryKey: ['lesson-transcripts', lessonId] }); },
    onError: e => toast.error(e.message),
  });

  const jobs = attached?.jobs || [];
  const unattached = (allJobs?.jobs || []).filter(j => j.link_id !== lessonId && (j.status === 'completed' || j.status === 'edited'));

  return (
    <Card data-testid="lesson-transcripts-panel">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center justify-between gap-2">
          <span className="flex items-center gap-2"><FileText className="h-4 w-4" /> Lesson transcripts &amp; captions</span>
          <Badge variant="outline">{jobs.length}</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : jobs.length === 0 ? (
          <p className="text-xs text-slate-400 italic">No transcripts attached yet — upload a recording or attach an existing transcription job.</p>
        ) : (
          <div className="space-y-2">
            {jobs.map(j => (
              <div key={j.id} className="rounded border border-slate-200 p-3" data-testid={`lesson-transcript-${j.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">{j.title}</p>
                    <p className="text-[10px] text-slate-500">{j.engine} · {j.detected_language || 'auto'} · {j.segment_count || 0} segments · {j.status}</p>
                    {j.summary && <p className="text-xs text-slate-600 mt-1 whitespace-pre-wrap line-clamp-4">{j.summary.slice(0, 400)}{j.summary.length > 400 ? '…' : ''}</p>}
                  </div>
                  <div className="flex gap-1">
                    {!j.summary && (j.status === 'completed' || j.status === 'edited') && (
                      <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => summarise.mutate(j.id)} disabled={summarise.isPending} data-testid={`summarize-${j.id}`}>
                        <Sparkles className="h-3 w-3 mr-0.5" /> AI Summary
                      </Button>
                    )}
                    <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => window.open(`${apiUrl}/api/transcription/jobs/${j.id}/export.srt`, '_blank')}>SRT</Button>
                    <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => window.open(`${apiUrl}/api/transcription/jobs/${j.id}/export.txt`, '_blank')}>TXT</Button>
                    <Button size="icon" variant="ghost" className="h-7 w-7 text-red-400" onClick={() => detach.mutate(j.id)} data-testid={`detach-${j.id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="border-t pt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Upload recording &amp; transcribe</Label>
            <div className="flex gap-2 mt-1">
              <Input type="file" accept="audio/*,video/*" onChange={e => setUploadFile(e.target.files?.[0] || null)} className="flex-1" data-testid="lesson-transcript-file" />
              <Button size="sm" disabled={!uploadFile || uploadAndAttach.isPending} onClick={() => uploadAndAttach.mutate()} data-testid="lesson-transcript-upload">
                {uploadAndAttach.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Upload'}
              </Button>
            </div>
          </div>
          <div>
            <Label className="text-xs">Or attach an existing transcript</Label>
            <div className="flex gap-2 mt-1">
              <Button size="sm" variant="outline" onClick={() => setShowAttachAll(s => !s)} data-testid="lesson-transcript-attach-toggle">
                {showAttachAll ? 'Hide' : 'Browse'} catalog
              </Button>
              {showAttachAll && unattached.length > 0 && (
                <Select onValueChange={(v) => attachExisting.mutate(v)}>
                  <SelectTrigger className="flex-1" data-testid="lesson-transcript-attach-select"><SelectValue placeholder={`${unattached.length} available`} /></SelectTrigger>
                  <SelectContent>{unattached.map(j => <SelectItem key={j.id} value={j.id}>{j.title} ({j.detected_language || 'auto'})</SelectItem>)}</SelectContent>
                </Select>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};



// ==================== LESSONS TAB ====================
const LessonsTab = ({ tenantId, subjects, classes }) => {
  const qc = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [selectedLesson, setSelectedLesson] = useState(null);
  const defaults = { title: '', subject_id: '', class_ids: [], topic: '', content: '', is_published: false, is_catchup: false, tags: '', video_url: '' };
  const [form, setForm] = useState(defaults);

  const { data: lessons = [], isLoading } = useQuery({
    queryKey: ['school-lessons', tenantId],
    queryFn: () => api(`/tenants/${tenantId}/lessons`),
  });

  useEffect(() => {
    if (showForm && editing) {
      setForm({ ...editing, tags: (editing.tags || []).join(', '), class_ids: editing.class_ids || [] });
    } else if (showForm && !editing) {
      setForm(defaults);
    }
  }, [showForm, editing]);

  const createMutation = useMutation({
    mutationFn: (d) => api(`/tenants/${tenantId}/lessons`, { method: 'POST', body: JSON.stringify(d) }),
    onSuccess: () => { toast.success('Lesson created'); close(); },
    onError: e => toast.error(e.message),
  });

  const updateMutation = useMutation({
    mutationFn: (d) => api(`/tenants/${tenantId}/lessons/${editing.id}`, { method: 'PUT', body: JSON.stringify(d) }),
    onSuccess: () => { toast.success('Lesson updated'); close(); },
    onError: e => toast.error(e.message),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => api(`/tenants/${tenantId}/lessons/${id}`, { method: 'DELETE' }),
    onSuccess: () => { toast.success('Lesson deleted'); qc.invalidateQueries({ queryKey: ['school-lessons'] }); qc.invalidateQueries({ queryKey: ['lms-dashboard'] }); },
    onError: e => toast.error(e.message),
  });

  const togglePublish = useMutation({
    mutationFn: ({ id, val }) => api(`/tenants/${tenantId}/lessons/${id}`, { method: 'PUT', body: JSON.stringify({ is_published: val }) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['school-lessons'] }); qc.invalidateQueries({ queryKey: ['lms-dashboard'] }); },
    onError: e => toast.error(e.message),
  });

  const close = () => { setShowForm(false); setEditing(null); qc.invalidateQueries({ queryKey: ['school-lessons'] }); qc.invalidateQueries({ queryKey: ['lms-dashboard'] }); };

  const handleSave = () => {
    const payload = { ...form, tags: form.tags ? form.tags.split(',').map(t => t.trim()).filter(Boolean) : [] };
    editing ? updateMutation.mutate(payload) : createMutation.mutate(payload);
  };

  const openEdit = (lesson, e) => { e.stopPropagation(); setEditing(lesson); setShowForm(true); };

  // Lesson detail
  if (selectedLesson) {
    return (
      <div className="space-y-4">
        <button onClick={() => setSelectedLesson(null)} className="text-xs text-indigo-600 hover:underline flex items-center gap-1"><ArrowLeft className="h-3 w-3" /> Back to Lessons</button>
        <Card>
          <CardHeader>
            <div className="flex items-start justify-between">
              <div>
                <CardTitle>{selectedLesson.title}</CardTitle>
                <CardDescription>{selectedLesson.subject_name} {selectedLesson.topic ? `- ${selectedLesson.topic}` : ''}</CardDescription>
              </div>
              <div className="flex gap-2">
                {selectedLesson.is_published ? <Badge className="bg-green-100 text-green-700">Published</Badge> : <Badge variant="secondary">Draft</Badge>}
                {selectedLesson.is_catchup && <Badge className="bg-sky-100 text-sky-700">Catch-up</Badge>}
                <Button size="sm" variant="outline" onClick={() => { setEditing(selectedLesson); setShowForm(true); }}><Edit2 className="h-3.5 w-3.5 mr-1" />Edit</Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="prose prose-sm max-w-none text-slate-700 whitespace-pre-wrap">{selectedLesson.content || 'No content yet'}</div>
            {selectedLesson.video_url && (
              <div className="mt-4 p-3 bg-slate-50 rounded-lg flex items-center gap-2">
                <Play className="h-4 w-4 text-indigo-500" />
                <a href={selectedLesson.video_url} target="_blank" rel="noreferrer" className="text-sm text-indigo-600 hover:underline truncate">{selectedLesson.video_url}</a>
              </div>
            )}
            {selectedLesson.tags?.length > 0 && (
              <div className="mt-3 flex gap-1 flex-wrap">
                {selectedLesson.tags.map(tag => <Badge key={tag} variant="outline" className="text-xs"><Tag className="h-3 w-3 mr-1" />{tag}</Badge>)}
              </div>
            )}
            <div className="mt-3 flex gap-3 text-xs text-slate-400">
              <span>{selectedLesson.assignment_count} assignments linked</span>
              <span>{selectedLesson.quiz_count} quizzes linked</span>
            </div>
          </CardContent>
        </Card>

        {/* Transcripts attached to this lesson — students see SRT captions
            on the video and a searchable transcript card alongside. */}
        <LessonTranscriptsPanel tenantId={tenantId} lessonId={selectedLesson.id} />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="lessons-tab">
      <div className="flex justify-between items-center">
        <p className="text-sm text-slate-500">{lessons.length} lessons</p>
        <Button size="sm" onClick={() => { setEditing(null); setShowForm(true); }} data-testid="create-lesson-btn"><Plus className="h-4 w-4 mr-1" /> New Lesson</Button>
      </div>

      {isLoading ? <Loader2 className="h-5 w-5 animate-spin mx-auto" /> : lessons.length === 0 ? (
        <Card className="border-dashed"><CardContent className="py-12 text-center text-slate-400"><BookOpen className="h-8 w-8 mx-auto mb-2 opacity-50" />No lessons yet</CardContent></Card>
      ) : (
        <div className="space-y-2">
          {lessons.map(lesson => (
            <Card key={lesson.id} className="hover:border-indigo-200 transition-colors cursor-pointer" onClick={() => setSelectedLesson(lesson)} data-testid={`lesson-card-${lesson.id}`}>
              <CardContent className="p-4 flex items-center justify-between">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-10 h-10 rounded-lg bg-indigo-50 flex items-center justify-center shrink-0"><BookOpen className="h-5 w-5 text-indigo-500" /></div>
                  <div className="min-w-0">
                    <h4 className="font-medium text-slate-900 truncate">{lesson.title}</h4>
                    <p className="text-xs text-slate-500">{lesson.subject_name} {lesson.topic ? `- ${lesson.topic}` : ''}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <div className="flex items-center gap-1.5" onClick={e => e.stopPropagation()}>
                    <Switch checked={lesson.is_published} onCheckedChange={v => togglePublish.mutate({ id: lesson.id, val: v })} className="scale-75" />
                    <span className="text-[10px] text-slate-400">{lesson.is_published ? 'Live' : 'Draft'}</span>
                  </div>
                  {lesson.is_catchup && <Badge className="bg-sky-50 text-sky-600 text-[10px]">Catch-up</Badge>}
                  <span className="text-[10px] text-slate-400">{lesson.assignment_count}A {lesson.quiz_count}Q</span>
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={e => openEdit(lesson, e)} data-testid={`edit-lesson-${lesson.id}`}><Edit2 className="h-3.5 w-3.5" /></Button>
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={e => { e.stopPropagation(); if (window.confirm('Delete this lesson?')) deleteMutation.mutate(lesson.id); }}><Trash2 className="h-3.5 w-3.5 text-red-400" /></Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={showForm} onOpenChange={v => { if (!v) close(); else setShowForm(true); }}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? 'Edit' : 'New'} Lesson</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <div><Label>Title *</Label><Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Lesson title" data-testid="lesson-title-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Subject *</Label>
                <Select value={form.subject_id} onValueChange={v => setForm({ ...form, subject_id: v })}>
                  <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{subjects.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Topic</Label><Input value={form.topic} onChange={e => setForm({ ...form, topic: e.target.value })} placeholder="e.g. Algebra" /></div>
            </div>
            <div><Label>Classes</Label>
              <div className="flex flex-wrap gap-1 mt-1">
                {classes.map(c => (
                  <button key={c.id} onClick={() => setForm({ ...form, class_ids: form.class_ids.includes(c.id) ? form.class_ids.filter(id => id !== c.id) : [...form.class_ids, c.id] })}
                    className={`px-2 py-0.5 rounded-full text-xs border ${form.class_ids.includes(c.id) ? 'bg-indigo-100 border-indigo-300 text-indigo-700' : 'bg-slate-50 border-slate-200'}`}>{c.name}</button>
                ))}
              </div>
            </div>
            <div><Label>Content</Label><Textarea value={form.content} onChange={e => setForm({ ...form, content: e.target.value })} rows={5} placeholder="Lesson content..." /></div>
            <MediaUpload value={form.video_url} onChange={(url) => setForm({ ...form, video_url: url })} purpose="video" label="Lesson video" testId="lms-lesson-video-upload" />
            <div><Label>Tags (comma separated)</Label><Input value={form.tags} onChange={e => setForm({ ...form, tags: e.target.value })} placeholder="algebra, equations" /></div>
            <div className="flex gap-6">
              <div className="flex items-center gap-2"><Switch checked={form.is_published} onCheckedChange={v => setForm({ ...form, is_published: v })} /><Label>Published</Label></div>
              <div className="flex items-center gap-2"><Switch checked={form.is_catchup} onCheckedChange={v => setForm({ ...form, is_catchup: v })} /><Label>Catch-up Library</Label></div>
            </div>
          </div>
          <DialogFooter className="mt-3">
            <DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose>
            <Button onClick={handleSave} disabled={createMutation.isPending || updateMutation.isPending || !form.title || !form.subject_id} data-testid="submit-lesson-btn">
              {(createMutation.isPending || updateMutation.isPending) && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
              {editing ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Per-row grade entry. Local state isolates one student from another and
// preserves typed marks across re-renders of the surrounding table.
const AssignmentGradeRow = ({ sub, maxMarks, isPending, onGrade }) => {
  const initial = sub.status === 'graded'
    ? { marks: sub.marks ?? '', feedback: sub.feedback ?? '' }
    : { marks: '', feedback: '' };
  const [form, setForm] = useState(initial);
  const [editing, setEditing] = useState(sub.status !== 'graded');
  const pct = sub.marks != null && maxMarks ? Math.round((sub.marks / maxMarks) * 100) : null;
  return (
    <TableRow data-testid={`submission-${sub.id}`}>
      <TableCell className="font-medium text-sm">{sub.student_name}</TableCell>
      <TableCell className="text-xs text-slate-500">{sub.admission_number}</TableCell>
      <TableCell>
        <Badge className={sub.status === 'graded' ? 'bg-green-100 text-green-700' : sub.status === 'submitted' ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'}>{sub.status}</Badge>
      </TableCell>
      <TableCell className="text-xs text-slate-500">{sub.submitted_at ? new Date(sub.submitted_at).toLocaleDateString() : '-'}</TableCell>
      <TableCell className="text-sm text-slate-600 max-w-[150px] truncate">{sub.content || '-'}</TableCell>
      <TableCell>
        {sub.status === 'graded' && !editing ? (
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium">{sub.marks}/{maxMarks}</span>
            {pct != null && <Badge variant="outline" className="text-[10px]">{pct}%</Badge>}
            <button onClick={() => setEditing(true)} className="text-[10px] text-indigo-600 hover:underline" data-testid={`regrade-btn-${sub.id}`}>edit</button>
          </div>
        ) : (
          <div className="flex items-center gap-1">
            <Input
              type="number"
              className="w-16 h-7 text-xs"
              placeholder="Marks"
              min={0}
              max={maxMarks}
              value={form.marks}
              onChange={e => setForm(f => ({ ...f, marks: e.target.value }))}
              data-testid={`grade-marks-${sub.id}`}
            />
            <Button
              size="sm"
              className="h-7 text-xs"
              data-testid={`grade-btn-${sub.id}`}
              onClick={() => onGrade({ marks: parseFloat(form.marks), feedback: form.feedback })}
              disabled={isPending || form.marks === ''}
            >
              {sub.status === 'graded' ? 'Save' : 'Grade'}
            </Button>
          </div>
        )}
      </TableCell>
      <TableCell>
        {editing ? (
          <Input
            className="h-7 text-xs w-full max-w-[180px]"
            placeholder="Feedback (optional)"
            value={form.feedback}
            onChange={e => setForm(f => ({ ...f, feedback: e.target.value }))}
            data-testid={`grade-feedback-${sub.id}`}
          />
        ) : (
          <span className="text-xs text-slate-500">{sub.feedback || '-'}</span>
        )}
      </TableCell>
    </TableRow>
  );
};


// ==================== ASSIGNMENTS TAB ====================
const AssignmentsTab = ({ tenantId, subjects, classes }) => {
  const qc = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const defaults = { title: '', subject_id: '', class_ids: [], instructions: '', due_date: '', max_marks: 100, is_published: false, allow_late: false, available_from: '', available_until: '', grace_minutes: 0 };
  const [form, setForm] = useState(defaults);
  const [gradeForm, setGradeForm] = useState({ marks: '', feedback: '' });
  const [showReopen, setShowReopen] = useState(false);

  const { data: assignments = [], isLoading } = useQuery({
    queryKey: ['school-assignments', tenantId],
    queryFn: () => api(`/tenants/${tenantId}/assignments`),
  });

  const { data: assignmentDetail } = useQuery({
    queryKey: ['school-assignment-detail', tenantId, selectedAssignment?.id],
    queryFn: () => api(`/tenants/${tenantId}/assignments/${selectedAssignment.id}`),
    enabled: !!selectedAssignment?.id,
  });

  useEffect(() => {
    if (showForm && editing) {
      setForm({ title: editing.title, subject_id: editing.subject_id, class_ids: editing.class_ids || [], instructions: editing.instructions || '', due_date: editing.due_date || '', max_marks: editing.max_marks || 100, is_published: editing.is_published || false, allow_late: editing.allow_late || false, available_from: editing.available_from || '', available_until: editing.available_until || '', grace_minutes: editing.grace_minutes || 0 });
    } else if (showForm && !editing) {
      setForm(defaults);
    }
  }, [showForm, editing]);

  const createMutation = useMutation({
    mutationFn: d => api(`/tenants/${tenantId}/assignments`, { method: 'POST', body: JSON.stringify(d) }),
    onSuccess: () => { toast.success('Assignment created'); close(); },
    onError: e => toast.error(e.message),
  });

  const updateMutation = useMutation({
    mutationFn: d => api(`/tenants/${tenantId}/assignments/${editing.id}`, { method: 'PUT', body: JSON.stringify(d) }),
    onSuccess: () => { toast.success('Assignment updated'); close(); },
    onError: e => toast.error(e.message),
  });

  const deleteMutation = useMutation({
    mutationFn: id => api(`/tenants/${tenantId}/assignments/${id}`, { method: 'DELETE' }),
    onSuccess: () => { toast.success('Assignment deleted'); setSelectedAssignment(null); qc.invalidateQueries({ queryKey: ['school-assignments'] }); qc.invalidateQueries({ queryKey: ['lms-dashboard'] }); },
    onError: e => toast.error(e.message),
  });

  const gradeMutation = useMutation({
    mutationFn: ({ id, data }) => api(`/tenants/${tenantId}/submissions/${id}/grade`, { method: 'PUT', body: JSON.stringify(data) }),
    onSuccess: () => { toast.success('Graded'); setGradeForm({ marks: '', feedback: '' }); qc.invalidateQueries({ queryKey: ['school-assignment-detail'] }); qc.invalidateQueries({ queryKey: ['lms-dashboard'] }); },
    onError: e => toast.error(e.message),
  });

  const close = () => { setShowForm(false); setEditing(null); qc.invalidateQueries({ queryKey: ['school-assignments'] }); qc.invalidateQueries({ queryKey: ['lms-dashboard'] }); };

  const handleSave = () => { editing ? updateMutation.mutate(form) : createMutation.mutate(form); };
  const openEdit = (a, e) => { e?.stopPropagation(); setEditing(a); setShowForm(true); };

  // Assignment detail
  if (selectedAssignment && assignmentDetail) {
    return (
      <div className="space-y-4" data-testid="assignment-detail">
        <div className="flex items-center justify-between">
          <button onClick={() => setSelectedAssignment(null)} className="text-xs text-indigo-600 hover:underline flex items-center gap-1"><ArrowLeft className="h-3 w-3" /> Back</button>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => openEdit(assignmentDetail)} data-testid="edit-assignment-btn"><Edit2 className="h-3.5 w-3.5 mr-1" />Edit</Button>
            <Button size="sm" variant="outline" onClick={() => setShowReopen(true)} data-testid="assignment-reopen-btn">
              <Clock className="h-3.5 w-3.5 mr-1" />Reopen…
            </Button>
            <Button size="sm" variant="outline" className="text-red-500" onClick={() => { if (window.confirm('Delete this assignment and all submissions?')) deleteMutation.mutate(assignmentDetail.id); }}>
              <Trash2 className="h-3.5 w-3.5 mr-1" />Delete
            </Button>
          </div>
        </div>
        <AssignmentReadinessChecklist tenantId={tenantId} assignmentId={assignmentDetail.id} />
        <Card>
          <CardHeader>
            <div className="flex items-start justify-between">
              <div>
                <CardTitle>{assignmentDetail.title}</CardTitle>
                <CardDescription>{assignmentDetail.subject_name} | Due: {assignmentDetail.due_date || 'No deadline'} | Max: {assignmentDetail.max_marks}</CardDescription>
              </div>
              <div className="flex gap-2">
                {assignmentDetail.is_published ? <Badge className="bg-green-100 text-green-700">Published</Badge> : <Badge variant="secondary">Draft</Badge>}
                {assignmentDetail.allow_late && <Badge variant="outline" className="text-xs">Late OK</Badge>}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {assignmentDetail.instructions && <p className="text-sm text-slate-600 mb-4 whitespace-pre-wrap">{assignmentDetail.instructions}</p>}
            <h3 className="font-semibold text-sm mb-2">Submissions ({assignmentDetail.submissions?.length || 0})</h3>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Student</TableHead>
                  <TableHead>Adm No.</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Submitted</TableHead>
                  <TableHead>Content</TableHead>
                  <TableHead>Marks</TableHead>
                  <TableHead>Feedback</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(assignmentDetail.submissions || []).length === 0 ? (
                  <TableRow><TableCell colSpan={7} className="text-center text-slate-400 py-6">No submissions yet</TableCell></TableRow>
                ) : assignmentDetail.submissions.map(sub => (
                  <AssignmentGradeRow
                    key={sub.id}
                    sub={sub}
                    maxMarks={assignmentDetail.max_marks}
                    isPending={gradeMutation.isPending}
                    onGrade={(data) => gradeMutation.mutate({ id: sub.id, data })}
                  />
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
        <ReopenDialog
          open={showReopen}
          onOpenChange={setShowReopen}
          tenantId={tenantId}
          kind="assignment"
          itemId={assignmentDetail.id}
          itemTitle={assignmentDetail.title}
          students={(assignmentDetail.submissions || []).map((s) => ({ id: s.student_id, name: s.student_name }))}
          onSuccess={() => qc.invalidateQueries({ queryKey: ['school-assignments'] })}
        />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="assignments-tab">
      <div className="flex justify-between items-center">
        <p className="text-sm text-slate-500">{assignments.length} assignments</p>
        <Button size="sm" onClick={() => { setEditing(null); setShowForm(true); }} data-testid="create-assignment-btn"><Plus className="h-4 w-4 mr-1" /> New Assignment</Button>
      </div>

      {isLoading ? <Loader2 className="h-5 w-5 animate-spin mx-auto" /> : assignments.length === 0 ? (
        <Card className="border-dashed"><CardContent className="py-12 text-center text-slate-400"><ClipboardList className="h-8 w-8 mx-auto mb-2 opacity-50" />No assignments yet</CardContent></Card>
      ) : (
        <div className="space-y-2">
          {assignments.map(a => (
            <Card key={a.id} className="hover:border-indigo-200 transition-colors cursor-pointer" onClick={() => setSelectedAssignment(a)} data-testid={`assignment-card-${a.id}`}>
              <CardContent className="p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center shrink-0"><ClipboardList className="h-5 w-5 text-emerald-500" /></div>
                  <div>
                    <h4 className="font-medium text-slate-900">{a.title}</h4>
                    <p className="text-xs text-slate-500">{a.subject_name} | Due: {a.due_date || 'No deadline'}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {a.is_published ? <Badge className="bg-green-50 text-green-600 text-[10px]">Published</Badge> : <Badge variant="secondary" className="text-[10px]">Draft</Badge>}
                  <Badge variant="outline" className="text-[10px]">{a.submission_count} submitted</Badge>
                  <Badge variant="outline" className="text-[10px]">{a.graded_count} graded</Badge>
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={e => openEdit(a, e)}><Edit2 className="h-3.5 w-3.5" /></Button>
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={e => { e.stopPropagation(); if (window.confirm('Delete?')) deleteMutation.mutate(a.id); }}><Trash2 className="h-3.5 w-3.5 text-red-400" /></Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={showForm} onOpenChange={v => { if (!v) close(); else setShowForm(true); }}>
        <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? 'Edit' : 'New'} Assignment</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <div><Label>Title *</Label><Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} data-testid="assignment-title-input" /></div>
            <div><Label>Subject *</Label>
              <Select value={form.subject_id} onValueChange={v => setForm({ ...form, subject_id: v })}>
                <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{subjects.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Classes</Label>
              <div className="flex flex-wrap gap-1 mt-1">
                {classes.map(c => (
                  <button key={c.id} onClick={() => setForm({ ...form, class_ids: form.class_ids.includes(c.id) ? form.class_ids.filter(id => id !== c.id) : [...form.class_ids, c.id] })}
                    className={`px-2 py-0.5 rounded-full text-xs border ${form.class_ids.includes(c.id) ? 'bg-indigo-100 border-indigo-300 text-indigo-700' : 'bg-slate-50 border-slate-200'}`}>{c.name}</button>
                ))}
              </div>
            </div>
            <div><Label>Instructions</Label><Textarea value={form.instructions} onChange={e => setForm({ ...form, instructions: e.target.value })} rows={3} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Due Date</Label><Input type="date" value={form.due_date} onChange={e => setForm({ ...form, due_date: e.target.value })} /></div>
              <div><Label>Max Marks</Label><Input type="number" value={form.max_marks} onChange={e => setForm({ ...form, max_marks: parseFloat(e.target.value) || 100 })} /></div>
            </div>
            {/* Optional submission window — when both are blank the assignment
                behaves like the legacy "always available until due_date" flow.
                Setting either flips on the upcoming/active/closed state engine. */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="assignment-window">
              <div><Label className="text-xs text-slate-500">Available from (optional)</Label><Input type="datetime-local" value={form.available_from || ''} onChange={e => setForm({ ...form, available_from: e.target.value })} data-testid="assignment-available-from" /></div>
              <div><Label className="text-xs text-slate-500">Submission closes (optional)</Label><Input type="datetime-local" value={form.available_until || ''} onChange={e => setForm({ ...form, available_until: e.target.value })} data-testid="assignment-available-until" /></div>
              <div><Label className="text-xs text-slate-500">Grace minutes</Label><Input type="number" min="0" value={form.grace_minutes || 0} onChange={e => setForm({ ...form, grace_minutes: parseInt(e.target.value, 10) || 0 })} data-testid="assignment-grace" /></div>
            </div>
            <div className="flex gap-6">
              <div className="flex items-center gap-2"><Switch checked={form.is_published} onCheckedChange={v => setForm({ ...form, is_published: v })} /><Label>Published</Label></div>
              <div className="flex items-center gap-2"><Switch checked={form.allow_late} onCheckedChange={v => setForm({ ...form, allow_late: v })} /><Label>Allow Late</Label></div>
            </div>
          </div>
          <DialogFooter className="mt-3">
            <DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose>
            <Button onClick={handleSave} disabled={createMutation.isPending || updateMutation.isPending || !form.title || !form.subject_id} data-testid="submit-assignment-btn">
              {(createMutation.isPending || updateMutation.isPending) && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
              {editing ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ==================== QUIZZES TAB ====================
const QuizzesTab = ({ tenantId, subjects, classes }) => {
  const qc = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [selectedQuiz, setSelectedQuiz] = useState(null);
  const [form, setForm] = useState({ title: '', subject_id: '', class_ids: [], instructions: '', time_limit_minutes: 30, max_attempts: 1, pass_mark_percent: 50, is_published: false, questions: [], available_from: '', available_until: '', grace_minutes: 0 });
  const [qf, setQf] = useState({ question: '', type: 'mcq', options: ['', '', '', ''], correct_answer: '', points: 1 });
  const [showReopen, setShowReopen] = useState(false);

  const { data: quizzes = [], isLoading } = useQuery({
    queryKey: ['school-quizzes', tenantId],
    queryFn: () => api(`/tenants/${tenantId}/quizzes`),
  });

  const { data: quizDetail } = useQuery({
    queryKey: ['school-quiz-detail', tenantId, selectedQuiz?.id],
    queryFn: () => api(`/tenants/${tenantId}/quizzes/${selectedQuiz.id}?include_answers=true`),
    enabled: !!selectedQuiz?.id,
  });

  const { data: quizAttempts = [] } = useQuery({
    queryKey: ['quiz-attempts', tenantId, selectedQuiz?.id],
    queryFn: () => api(`/tenants/${tenantId}/quiz-attempts?quiz_id=${selectedQuiz.id}`),
    enabled: !!selectedQuiz?.id,
  });

  const createMutation = useMutation({
    mutationFn: d => api(`/tenants/${tenantId}/quizzes`, { method: 'POST', body: JSON.stringify(d) }),
    onSuccess: () => { toast.success('Quiz created'); setShowCreate(false); resetQuizForm(); qc.invalidateQueries({ queryKey: ['school-quizzes'] }); qc.invalidateQueries({ queryKey: ['lms-dashboard'] }); },
    onError: e => toast.error(e.message),
  });

  const deleteMutation = useMutation({
    mutationFn: id => api(`/tenants/${tenantId}/quizzes/${id}`, { method: 'DELETE' }),
    onSuccess: () => { toast.success('Quiz deleted'); setSelectedQuiz(null); qc.invalidateQueries({ queryKey: ['school-quizzes'] }); qc.invalidateQueries({ queryKey: ['lms-dashboard'] }); },
    onError: e => toast.error(e.message),
  });

  const resetQuizForm = () => { setForm({ title: '', subject_id: '', class_ids: [], instructions: '', time_limit_minutes: 30, max_attempts: 1, pass_mark_percent: 50, is_published: false, questions: [], available_from: '', available_until: '', grace_minutes: 0 }); setQf({ question: '', type: 'mcq', options: ['', '', '', ''], correct_answer: '', points: 1 }); };

  const addQuestion = () => {
    setForm(f => ({ ...f, questions: [...f.questions, { ...qf, options: qf.type === 'mcq' ? qf.options.filter(Boolean) : [] }] }));
    setQf({ question: '', type: 'mcq', options: ['', '', '', ''], correct_answer: '', points: 1 });
  };

  // Quiz detail view
  if (selectedQuiz) {
    const detail = quizDetail || selectedQuiz;
    // Pull unique attempted-student list for the Reopen "per-student" picker.
    const attemptStudents = Array.from(
      new Map(quizAttempts.map((a) => [a.student_id, { id: a.student_id, name: a.student_name }])).values()
    );
    return (
      <div className="space-y-4" data-testid="quiz-detail">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <button onClick={() => setSelectedQuiz(null)} className="text-xs text-indigo-600 hover:underline flex items-center gap-1"><ArrowLeft className="h-3 w-3" /> Back to Quizzes</button>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => setShowReopen(true)} data-testid="quiz-reopen-btn">
              <Clock className="h-3.5 w-3.5 mr-1" />Reopen…
            </Button>
            <Button size="sm" variant="outline" className="text-red-500" onClick={() => { if (window.confirm('Delete this quiz and all attempts?')) deleteMutation.mutate(detail.id); }}>
              <Trash2 className="h-3.5 w-3.5 mr-1" />Delete
            </Button>
          </div>
        </div>
        <QuizReadinessChecklist tenantId={tenantId} quizId={detail.id} />
        <Card>
          <CardHeader>
            <div className="flex items-start justify-between">
              <div>
                <CardTitle>{detail.title}</CardTitle>
                <CardDescription>{detail.subject_name} | {(detail.questions || []).length} questions | Pass: {detail.pass_mark_percent}%</CardDescription>
              </div>
              <div className="flex gap-2">
                {detail.is_published ? <Badge className="bg-green-100 text-green-700">Published</Badge> : <Badge variant="secondary">Draft</Badge>}
                {detail.time_limit_minutes && <Badge variant="outline" className="text-xs"><Clock className="h-3 w-3 mr-1" />{detail.time_limit_minutes} min</Badge>}
                <Badge variant="outline" className="text-xs">Max {detail.max_attempts} attempt{detail.max_attempts !== 1 ? 's' : ''}</Badge>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {detail.instructions && <p className="text-sm text-slate-600">{detail.instructions}</p>}

            {/* Questions */}
            <div>
              <h3 className="font-semibold text-sm mb-2">Questions</h3>
              {(detail.questions || []).map((q, i) => (
                <div key={i} className="flex items-start gap-2 bg-slate-50 p-3 rounded-lg mb-2">
                  <span className="text-xs text-slate-400 font-medium mt-0.5">Q{i + 1}</span>
                  <div className="flex-1">
                    <p className="text-sm font-medium">{q.question}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <Badge variant="outline" className="text-[10px]">{q.type}</Badge>
                      <span className="text-[10px] text-slate-400">{q.points} pt{q.points !== 1 ? 's' : ''}</span>
                      {q.correct_answer && <span className="text-[10px] text-green-600">Answer: {q.correct_answer}</span>}
                    </div>
                    {q.options?.length > 0 && <div className="flex flex-wrap gap-1 mt-1">{q.options.map((o, oi) => <Badge key={oi} variant="outline" className="text-[10px]">{o}</Badge>)}</div>}
                  </div>
                </div>
              ))}
            </div>

            {/* Attempts */}
            <div>
              <h3 className="font-semibold text-sm mb-2">Student Attempts ({quizAttempts.length})</h3>
              {quizAttempts.length === 0 ? (
                <p className="text-sm text-slate-400">No attempts yet</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Student</TableHead>
                      <TableHead>Attempt #</TableHead>
                      <TableHead>Score</TableHead>
                      <TableHead>%</TableHead>
                      <TableHead>Result</TableHead>
                      <TableHead>Date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {quizAttempts.map(a => (
                      <TableRow key={a.id} data-testid={`attempt-${a.id}`}>
                        <TableCell className="font-medium text-sm">{a.student_name}</TableCell>
                        <TableCell className="text-sm">{a.attempt_number}</TableCell>
                        <TableCell className="text-sm">{a.earned_points}/{a.total_points}</TableCell>
                        <TableCell className="font-medium text-sm">{a.score_percent}%</TableCell>
                        <TableCell>{a.passed ? <Badge className="bg-green-100 text-green-700 text-xs">Passed</Badge> : <Badge className="bg-red-100 text-red-700 text-xs">Failed</Badge>}</TableCell>
                        <TableCell className="text-xs text-slate-500">{a.submitted_at ? new Date(a.submitted_at).toLocaleDateString() : '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          </CardContent>
        </Card>
        <ReopenDialog
          open={showReopen}
          onOpenChange={setShowReopen}
          tenantId={tenantId}
          kind="quiz"
          itemId={detail.id}
          itemTitle={detail.title}
          students={attemptStudents}
          onSuccess={() => qc.invalidateQueries({ queryKey: ['school-quizzes'] })}
        />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="quizzes-tab">
      <div className="flex justify-between items-center">
        <p className="text-sm text-slate-500">{quizzes.length} quizzes</p>
        <Button size="sm" onClick={() => { resetQuizForm(); setShowCreate(true); }} data-testid="create-quiz-btn"><Plus className="h-4 w-4 mr-1" /> New Quiz</Button>
      </div>

      {isLoading ? <Loader2 className="h-5 w-5 animate-spin mx-auto" /> : quizzes.length === 0 ? (
        <Card className="border-dashed"><CardContent className="py-12 text-center text-slate-400"><Award className="h-8 w-8 mx-auto mb-2 opacity-50" />No quizzes yet</CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {quizzes.map(q => (
            <Card key={q.id} className="hover:border-indigo-200 transition-colors cursor-pointer" onClick={() => setSelectedQuiz(q)} data-testid={`quiz-card-${q.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <h4 className="font-semibold text-slate-900">{q.title}</h4>
                  <div className="flex gap-1">
                    {q.is_published ? <Badge className="bg-green-100 text-green-700 text-[10px]">Published</Badge> : <Badge variant="secondary" className="text-[10px]">Draft</Badge>}
                  </div>
                </div>
                <p className="text-xs text-slate-500 mb-2">{q.subject_name}</p>
                <div className="flex gap-3 text-xs text-slate-500">
                  <span>{q.question_count} questions</span>
                  <span>{q.attempt_count} attempts</span>
                  {q.time_limit_minutes && <span><Clock className="h-3 w-3 inline mr-0.5" />{q.time_limit_minutes}min</span>}
                  <span>Pass: {q.pass_mark_percent}%</span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create Quiz Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>New Quiz</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Title *</Label><Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} data-testid="quiz-title-input" /></div>
              <div><Label>Subject *</Label>
                <Select value={form.subject_id} onValueChange={v => setForm({ ...form, subject_id: v })}>
                  <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{subjects.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <div><Label>Time Limit (min)</Label><Input type="number" value={form.time_limit_minutes} onChange={e => setForm({ ...form, time_limit_minutes: parseInt(e.target.value) || null })} /></div>
              <div><Label>Max Attempts</Label><Input type="number" value={form.max_attempts} onChange={e => setForm({ ...form, max_attempts: parseInt(e.target.value) || 1 })} /></div>
              <div><Label>Pass Mark %</Label><Input type="number" value={form.pass_mark_percent} onChange={e => setForm({ ...form, pass_mark_percent: parseFloat(e.target.value) || 50 })} /></div>
            </div>
            <div><Label>Classes</Label>
              <div className="flex flex-wrap gap-1 mt-1">
                {classes.map(c => (
                  <button key={c.id} onClick={() => setForm({ ...form, class_ids: form.class_ids.includes(c.id) ? form.class_ids.filter(id => id !== c.id) : [...form.class_ids, c.id] })}
                    className={`px-2 py-0.5 rounded-full text-xs border ${form.class_ids.includes(c.id) ? 'bg-indigo-100 border-indigo-300 text-indigo-700' : 'bg-slate-50 border-slate-200'}`}>{c.name}</button>
                ))}
              </div>
            </div>

            {/* Optional scheduling window — when both timestamps are blank
                the quiz behaves like the legacy always-on flow. Either field
                flips on the upcoming/active/closed state engine + enforces
                the window on POST /quiz-attempts. */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="quiz-window">
              <div><Label className="text-xs text-slate-500">Available from</Label><Input type="datetime-local" value={form.available_from || ''} onChange={e => setForm({ ...form, available_from: e.target.value })} data-testid="quiz-available-from" /></div>
              <div><Label className="text-xs text-slate-500">Closes at</Label><Input type="datetime-local" value={form.available_until || ''} onChange={e => setForm({ ...form, available_until: e.target.value })} data-testid="quiz-available-until" /></div>
              <div><Label className="text-xs text-slate-500">Grace minutes</Label><Input type="number" min="0" value={form.grace_minutes || 0} onChange={e => setForm({ ...form, grace_minutes: parseInt(e.target.value, 10) || 0 })} data-testid="quiz-grace" /></div>
            </div>

            <div><Label>Instructions</Label><Textarea value={form.instructions} onChange={e => setForm({ ...form, instructions: e.target.value })} rows={2} /></div>
            <div className="border-t pt-3">
              <h3 className="font-semibold text-sm mb-2">Questions ({form.questions.length})</h3>
              {form.questions.map((q, i) => (
                <div key={i} className="flex items-start gap-2 bg-slate-50 p-2 rounded mb-1">
                  <span className="text-xs text-slate-400 mt-1">Q{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{q.question}</p>
                    <p className="text-xs text-slate-500">{q.type} | {q.points}pt | Answer: {q.correct_answer}</p>
                  </div>
                  <Button variant="ghost" size="icon" className="h-6 w-6 shrink-0" onClick={() => setForm({ ...form, questions: form.questions.filter((_, idx) => idx !== i) })}>
                    <Trash2 className="h-3 w-3 text-red-400" />
                  </Button>
                </div>
              ))}
              <Card className="mt-3">
                <CardContent className="p-3 space-y-2">
                  <div><Label className="text-xs">Question</Label><Input value={qf.question} onChange={e => setQf({ ...qf, question: e.target.value })} placeholder="Enter question" /></div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    <div><Label className="text-xs">Type</Label>
                      <Select value={qf.type} onValueChange={v => setQf({ ...qf, type: v })}>
                        <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="mcq">MCQ</SelectItem><SelectItem value="true_false">True/False</SelectItem><SelectItem value="short_answer">Short Answer</SelectItem></SelectContent>
                      </Select>
                    </div>
                    <div><Label className="text-xs">Correct Answer</Label><Input className="h-8" value={qf.correct_answer} onChange={e => setQf({ ...qf, correct_answer: e.target.value })} /></div>
                    <div><Label className="text-xs">Points</Label><Input className="h-8" type="number" value={qf.points} onChange={e => setQf({ ...qf, points: parseInt(e.target.value) || 1 })} /></div>
                  </div>
                  {qf.type === 'mcq' && (
                    <div className="grid grid-cols-2 gap-2">
                      {qf.options.map((opt, i) => (
                        <Input key={i} className="h-8 text-xs" value={opt} onChange={e => { const opts = [...qf.options]; opts[i] = e.target.value; setQf({ ...qf, options: opts }); }} placeholder={`Option ${String.fromCharCode(65 + i)}`} />
                      ))}
                    </div>
                  )}
                  <Button size="sm" variant="outline" onClick={addQuestion} disabled={!qf.question || !qf.correct_answer} data-testid="add-question-btn"><Plus className="h-3 w-3 mr-1" /> Add Question</Button>
                </CardContent>
              </Card>
            </div>
            <div className="flex items-center gap-2"><Switch checked={form.is_published} onCheckedChange={v => setForm({ ...form, is_published: v })} /><Label>Published</Label></div>
          </div>
          <DialogFooter className="mt-3">
            <DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose>
            <Button onClick={() => createMutation.mutate(form)} disabled={createMutation.isPending || !form.title || !form.subject_id || !form.questions.length} data-testid="submit-quiz-btn">
              {createMutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />} Create Quiz
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ==================== LIVE CLASSES TAB ====================
const LiveClassesTab = ({ tenantId, subjects, classes, teachers }) => {
  const qc = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [showRecording, setShowRecording] = useState(null);
  const [recordingUrl, setRecordingUrl] = useState('');
  const [form, setForm] = useState({ title: '', subject_id: '', class_ids: [], teacher_id: '', scheduled_at: '', duration_minutes: 45, meeting_url: '', meeting_platform: 'zoom', description: '' });

  const { data: liveClasses = [], isLoading } = useQuery({
    queryKey: ['school-live-classes', tenantId],
    queryFn: () => api(`/tenants/${tenantId}/live-classes`),
  });

  const createMutation = useMutation({
    mutationFn: d => api(`/tenants/${tenantId}/live-classes`, { method: 'POST', body: JSON.stringify(d) }),
    onSuccess: () => { toast.success('Live class scheduled'); setShowCreate(false); qc.invalidateQueries({ queryKey: ['school-live-classes'] }); qc.invalidateQueries({ queryKey: ['lms-dashboard'] }); },
    onError: e => toast.error(e.message),
  });

  const statusMutation = useMutation({
    mutationFn: ({ id, status }) => api(`/tenants/${tenantId}/live-classes/${id}/status?status=${status}`, { method: 'PUT' }),
    onSuccess: () => { toast.success('Status updated'); qc.invalidateQueries({ queryKey: ['school-live-classes'] }); qc.invalidateQueries({ queryKey: ['lms-dashboard'] }); },
    onError: e => toast.error(e.message),
  });

  const recordingMutation = useMutation({
    mutationFn: ({ id, url }) => api(`/tenants/${tenantId}/live-classes/${id}/recording?recording_url=${encodeURIComponent(url)}`, { method: 'PUT' }),
    onSuccess: () => { toast.success('Recording URL saved'); setShowRecording(null); setRecordingUrl(''); qc.invalidateQueries({ queryKey: ['school-live-classes'] }); },
    onError: e => toast.error(e.message),
  });

  const deleteMutation = useMutation({
    mutationFn: id => api(`/tenants/${tenantId}/live-classes/${id}`, { method: 'DELETE' }),
    onSuccess: () => { toast.success('Live class deleted'); qc.invalidateQueries({ queryKey: ['school-live-classes'] }); qc.invalidateQueries({ queryKey: ['lms-dashboard'] }); },
    onError: e => toast.error(e.message),
  });

  const statusColors = { scheduled: 'bg-blue-100 text-blue-700', live: 'bg-red-100 text-red-700', completed: 'bg-green-100 text-green-700', cancelled: 'bg-slate-100 text-slate-500' };
  const platforms = { zoom: 'Zoom', meet: 'Google Meet', teams: 'Teams', custom: 'Custom' };
  const nextStatus = { scheduled: 'live', live: 'completed' };
  const statusAction = { scheduled: 'Go Live', live: 'Complete' };

  return (
    <div className="space-y-4" data-testid="live-classes-tab">
      <div className="flex justify-between items-center">
        <p className="text-sm text-slate-500">{liveClasses.length} sessions</p>
        <Button size="sm" onClick={() => setShowCreate(true)} data-testid="create-live-class-btn"><Plus className="h-4 w-4 mr-1" /> Schedule Class</Button>
      </div>

      {isLoading ? <Loader2 className="h-5 w-5 animate-spin mx-auto" /> : liveClasses.length === 0 ? (
        <Card className="border-dashed"><CardContent className="py-12 text-center text-slate-400"><Video className="h-8 w-8 mx-auto mb-2 opacity-50" />No live classes scheduled</CardContent></Card>
      ) : (
        <div className="space-y-2">
          {liveClasses.map(lc => (
            <Card key={lc.id} data-testid={`live-class-${lc.id}`}>
              <CardContent className="p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${lc.status === 'live' ? 'bg-red-50' : 'bg-rose-50'}`}>
                    {lc.status === 'live' ? <Radio className="h-5 w-5 text-red-500 animate-pulse" /> : <Video className="h-5 w-5 text-rose-500" />}
                  </div>
                  <div>
                    <h4 className="font-medium text-slate-900">{lc.title}</h4>
                    <p className="text-xs text-slate-500">{lc.subject_name} | {lc.teacher_name} | {new Date(lc.scheduled_at).toLocaleString()}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Badge className={statusColors[lc.status] || ''}>{lc.status}</Badge>
                  <Badge variant="outline" className="text-[10px]">{platforms[lc.meeting_platform] || lc.meeting_platform}</Badge>

                  {/* Status transitions */}
                  {nextStatus[lc.status] && (
                    <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => statusMutation.mutate({ id: lc.id, status: nextStatus[lc.status] })} data-testid={`status-${lc.id}`}>
                      {statusAction[lc.status]}
                    </Button>
                  )}
                  {lc.status === 'scheduled' && (
                    <Button size="sm" variant="outline" className="h-7 text-xs text-red-500" onClick={() => statusMutation.mutate({ id: lc.id, status: 'cancelled' })}>Cancel</Button>
                  )}

                  {/* Meeting URL */}
                  {lc.meeting_url && lc.status !== 'completed' && lc.status !== 'cancelled' && (
                    <a href={lc.meeting_url} target="_blank" rel="noreferrer"><Button size="sm" variant="outline" className="h-7 text-xs">Join</Button></a>
                  )}

                  {/* Recording */}
                  {lc.status === 'completed' && (
                    lc.recording_url ? (
                      <a href={lc.recording_url} target="_blank" rel="noreferrer"><Button size="sm" variant="ghost" className="h-7 text-xs"><Play className="h-3 w-3 mr-1" />Watch</Button></a>
                    ) : (
                      <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => { setShowRecording(lc.id); setRecordingUrl(''); }} data-testid={`add-recording-${lc.id}`}>
                        <Link2 className="h-3 w-3 mr-1" />Add Recording
                      </Button>
                    )
                  )}

                  {/* Delete */}
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { if (window.confirm('Delete this class?')) deleteMutation.mutate(lc.id); }}>
                    <Trash2 className="h-3.5 w-3.5 text-red-400" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Recording URL Dialog */}
      <Dialog open={!!showRecording} onOpenChange={() => setShowRecording(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Add Recording</DialogTitle></DialogHeader>
          <div className="mt-2">
            <MediaUpload value={recordingUrl} onChange={setRecordingUrl} purpose="video" label="Recording" testId="recording-upload" />
          </div>
          <DialogFooter className="mt-3">
            <DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose>
            <Button onClick={() => recordingMutation.mutate({ id: showRecording, url: recordingUrl })} disabled={!recordingUrl || recordingMutation.isPending} data-testid="save-recording-btn">
              {recordingMutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />} Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Schedule Live Class</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <div><Label>Title *</Label><Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} data-testid="live-class-title-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Subject *</Label>
                <Select value={form.subject_id} onValueChange={v => setForm({ ...form, subject_id: v })}>
                  <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{subjects.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Teacher *</Label>
                <Select value={form.teacher_id} onValueChange={v => setForm({ ...form, teacher_id: v })}>
                  <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{teachers.map(t => <SelectItem key={t.id} value={t.id}>{t.first_name} {t.last_name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Classes</Label>
              <div className="flex flex-wrap gap-1 mt-1">
                {classes.map(c => (
                  <button key={c.id} onClick={() => setForm({ ...form, class_ids: form.class_ids.includes(c.id) ? form.class_ids.filter(id => id !== c.id) : [...form.class_ids, c.id] })}
                    className={`px-2 py-0.5 rounded-full text-xs border ${form.class_ids.includes(c.id) ? 'bg-indigo-100 border-indigo-300 text-indigo-700' : 'bg-slate-50 border-slate-200'}`}>{c.name}</button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Date & Time *</Label><Input type="datetime-local" value={form.scheduled_at} onChange={e => setForm({ ...form, scheduled_at: e.target.value })} /></div>
              <div><Label>Duration (min)</Label><Input type="number" value={form.duration_minutes} onChange={e => setForm({ ...form, duration_minutes: parseInt(e.target.value) || 45 })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Platform</Label>
                <Select value={form.meeting_platform} onValueChange={v => setForm({ ...form, meeting_platform: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="zoom">Zoom</SelectItem><SelectItem value="meet">Google Meet</SelectItem><SelectItem value="teams">Teams</SelectItem><SelectItem value="custom">Custom</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label>Meeting URL</Label><Input value={form.meeting_url} onChange={e => setForm({ ...form, meeting_url: e.target.value })} placeholder="https://..." /></div>
            </div>
            <div><Label>Description</Label><Textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={2} /></div>
          </div>
          <DialogFooter className="mt-3">
            <DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose>
            <Button onClick={() => createMutation.mutate(form)} disabled={createMutation.isPending || !form.title || !form.subject_id || !form.teacher_id || !form.scheduled_at} data-testid="submit-live-class-btn">
              {createMutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-1" />} Schedule
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ==================== CATCH-UP TAB ====================
const CatchUpTab = ({ tenantId }) => {
  const [search, setSearch] = useState('');
  const [selectedTag, setSelectedTag] = useState('');

  const { data: catchup, isLoading } = useQuery({
    queryKey: ['school-catchup', tenantId, search, selectedTag],
    queryFn: () => api(`/tenants/${tenantId}/catchup${search ? `?search=${search}` : ''}${selectedTag ? `${search ? '&' : '?'}tag=${selectedTag}` : ''}`),
  });

  return (
    <div className="space-y-4" data-testid="catchup-tab">
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input className="pl-10" placeholder="Search lessons..." value={search} onChange={e => setSearch(e.target.value)} data-testid="catchup-search" />
        </div>
        {catchup?.tags?.length > 0 && (
          <div className="flex gap-1 flex-wrap">
            <button onClick={() => setSelectedTag('')} className={`px-2 py-0.5 rounded-full text-xs border ${!selectedTag ? 'bg-indigo-100 border-indigo-300 text-indigo-700' : 'bg-slate-50 border-slate-200'}`}>All</button>
            {catchup.tags.map(tag => (
              <button key={tag} onClick={() => setSelectedTag(tag === selectedTag ? '' : tag)}
                className={`px-2 py-0.5 rounded-full text-xs border ${selectedTag === tag ? 'bg-indigo-100 border-indigo-300 text-indigo-700' : 'bg-slate-50 border-slate-200'}`}>{tag}</button>
            ))}
          </div>
        )}
      </div>

      {isLoading ? <Loader2 className="h-5 w-5 animate-spin mx-auto" /> : (!catchup?.lessons?.length && !catchup?.recordings?.length) ? (
        <Card className="border-dashed"><CardContent className="py-12 text-center text-slate-400"><GraduationCap className="h-8 w-8 mx-auto mb-2 opacity-50" />No catch-up content available yet</CardContent></Card>
      ) : (
        <div className="space-y-3">
          {catchup?.lessons?.map(item => (
            <Card key={item.id}>
              <CardContent className="p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-sky-50 flex items-center justify-center shrink-0"><BookOpen className="h-5 w-5 text-sky-500" /></div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-medium text-slate-900">{item.title}</h4>
                  <p className="text-xs text-slate-500">{item.subject_name} {item.topic ? `- ${item.topic}` : ''}</p>
                  {item.tags?.length > 0 && <div className="flex gap-1 mt-1">{item.tags.map(t => <Badge key={t} variant="outline" className="text-[10px]">{t}</Badge>)}</div>}
                </div>
                {item.video_url && <Badge className="bg-purple-100 text-purple-700 text-xs shrink-0"><Play className="h-3 w-3 mr-0.5" />Video</Badge>}
              </CardContent>
            </Card>
          ))}
          {catchup?.recordings?.map(item => (
            <Card key={item.id}>
              <CardContent className="p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-rose-50 flex items-center justify-center shrink-0"><Video className="h-5 w-5 text-rose-500" /></div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-medium text-slate-900">{item.title}</h4>
                  <p className="text-xs text-slate-500">{item.subject_name} | {item.teacher_name} | {new Date(item.scheduled_at).toLocaleDateString()}</p>
                </div>
                {item.recording_url && <a href={item.recording_url} target="_blank" rel="noreferrer"><Button size="sm" variant="outline" className="text-xs"><Play className="h-3 w-3 mr-1" />Watch</Button></a>}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

// ==================== MAIN LMS COMPONENT ====================
export const SchoolLMS = ({ tenantId }) => {
  const { data: subjects = [] } = useQuery({ queryKey: ['school-subjects', tenantId], queryFn: () => api(`/tenants/${tenantId}/subjects`) });
  const { data: classes = [] } = useQuery({ queryKey: ['school-classes', tenantId], queryFn: () => api(`/tenants/${tenantId}/classes`) });
  const { data: teachers = [] } = useQuery({ queryKey: ['school-teachers', tenantId], queryFn: () => api(`/tenants/${tenantId}/teachers`) });

  return (
    <div className="space-y-6" data-testid="lms-panel">
      <h2 className="text-base font-semibold text-slate-900 flex items-center gap-2">
        <GraduationCap className="h-5 w-5" /> Learning Management System
      </h2>
      <LMSDashboard tenantId={tenantId} />
      <Tabs defaultValue="lessons">
        <TabsList className="flex-wrap">
          <TabsTrigger value="lessons" data-testid="lessons-tab-trigger"><BookOpen className="h-4 w-4 mr-1" /> Lessons</TabsTrigger>
          <TabsTrigger value="assignments" data-testid="assignments-tab-trigger"><ClipboardList className="h-4 w-4 mr-1" /> Assignments</TabsTrigger>
          <TabsTrigger value="quizzes" data-testid="quizzes-tab-trigger"><Award className="h-4 w-4 mr-1" /> Quizzes</TabsTrigger>
          <TabsTrigger value="live" data-testid="live-tab-trigger"><Video className="h-4 w-4 mr-1" /> Live Classes</TabsTrigger>
          <TabsTrigger value="catchup" data-testid="catchup-tab-trigger"><Play className="h-4 w-4 mr-1" /> Catch-up</TabsTrigger>
          <TabsTrigger value="pop-quiz" data-testid="popquiz-tab-trigger"><Sparkles className="h-4 w-4 mr-1" /> Pop-Quiz Links</TabsTrigger>
          <TabsTrigger value="weekly" data-testid="weekly-tab-trigger"><CalendarDays className="h-4 w-4 mr-1" /> Weekly Recap</TabsTrigger>
          <TabsTrigger value="lecturer" data-testid="lecturer-tab-trigger"><BarChart3 className="h-4 w-4 mr-1" /> Lecturer Dashboard</TabsTrigger>
          <TabsTrigger value="live-settings" data-testid="live-settings-tab-trigger"><Settings2 className="h-4 w-4 mr-1" /> Live Settings</TabsTrigger>
          <TabsTrigger value="lesson-plans" data-testid="lesson-plans-tab-trigger"><BookMarked className="h-4 w-4 mr-1" /> Lesson Plans</TabsTrigger>
          <TabsTrigger value="syllabus" data-testid="syllabus-tab-trigger"><TrendingUp className="h-4 w-4 mr-1" /> Syllabus Progress</TabsTrigger>
        <TabsContent value="pop-quiz" className="mt-4"><PopQuizLinksTab tenantId={tenantId} /></TabsContent>
        <TabsContent value="weekly" className="mt-4"><WeeklyRecapTab tenantId={tenantId} subjects={subjects} classes={classes} /></TabsContent>
        <TabsContent value="lecturer" className="mt-4"><LecturerDashboardTab tenantId={tenantId} teachers={teachers} /></TabsContent>
        <TabsContent value="live-settings" className="mt-4"><LiveSettingsTab tenantId={tenantId} /></TabsContent>
        <TabsContent value="lesson-plans" className="mt-4"><LessonPlansTab tenantId={tenantId} subjects={subjects} /></TabsContent>
        <TabsContent value="syllabus" className="mt-4"><SyllabusProgressTab tenantId={tenantId} subjects={subjects} classes={classes} /></TabsContent>
        </TabsList>
        <TabsContent value="lessons" className="mt-4"><LessonsTab tenantId={tenantId} subjects={subjects} classes={classes} /></TabsContent>
        <TabsContent value="assignments" className="mt-4"><AssignmentsTab tenantId={tenantId} subjects={subjects} classes={classes} /></TabsContent>
        <TabsContent value="quizzes" className="mt-4"><QuizzesTab tenantId={tenantId} subjects={subjects} classes={classes} /></TabsContent>
        <TabsContent value="live" className="mt-4"><LiveClassesTab tenantId={tenantId} subjects={subjects} classes={classes} teachers={teachers} /></TabsContent>
        <TabsContent value="catchup" className="mt-4"><CatchUpTab tenantId={tenantId} /></TabsContent>
      </Tabs>
    </div>
  );
};

// ==================== PHASE A — POP-QUIZ LINKS TAB ====================
const PopQuizLinksTab = ({ tenantId }) => {
  const qc = useQueryClient();
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ lesson_id: '', quiz_id: '', required_watch_percent: 80, is_mandatory: true });
  const { data: links = [] } = useQuery({ queryKey: ['pop-quiz-links', tenantId], queryFn: () => api(`/tenants/${tenantId}/lms/pop-quiz-links`) });
  const { data: lessons = [] } = useQuery({ queryKey: ['pq-lessons', tenantId], queryFn: () => api(`/tenants/${tenantId}/lessons?is_catchup=true`) });
  const { data: quizzes = [] } = useQuery({ queryKey: ['pq-quizzes', tenantId], queryFn: () => api(`/tenants/${tenantId}/quizzes`) });
  const mut = useMutation({
    mutationFn: () => api(`/tenants/${tenantId}/lms/pop-quiz-links`, { method: 'POST', body: JSON.stringify(form) }),
    onSuccess: () => { toast.success('Pop-quiz linked'); qc.invalidateQueries({ queryKey: ['pop-quiz-links'] }); setShow(false); setForm({ lesson_id: '', quiz_id: '', required_watch_percent: 80, is_mandatory: true }); },
    onError: e => toast.error(e.message),
  });
  const del = useMutation({
    mutationFn: id => api(`/tenants/${tenantId}/lms/pop-quiz-links/${id}`, { method: 'DELETE' }),
    onSuccess: () => { toast.success('Removed'); qc.invalidateQueries({ queryKey: ['pop-quiz-links'] }); },
  });
  return (
    <div data-testid="pop-quiz-links-tab">
      <div className="flex justify-between items-center mb-3">
        <div>
          <h3 className="text-base font-semibold">Pop-Quiz → Lesson Links</h3>
          <p className="text-xs text-slate-500">Lock a short quiz behind a recorded lesson. Students must watch X% before they can take the quiz.</p>
        </div>
        <Button size="sm" onClick={() => setShow(true)} data-testid="new-pop-quiz-link"><Plus className="h-3.5 w-3.5 mr-1" />Link Pop-Quiz</Button>
      </div>
      <Card><CardContent className="p-0"><Table>
        <TableHeader><TableRow><TableHead>Lesson</TableHead><TableHead>Quiz</TableHead><TableHead>Watch %</TableHead><TableHead>Mandatory</TableHead><TableHead></TableHead></TableRow></TableHeader>
        <TableBody>
          {links.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-slate-400 py-8">No pop-quiz links yet.</TableCell></TableRow>}
          {links.map(l => (
            <TableRow key={l.id} data-testid={`pop-quiz-link-${l.id}`}>
              <TableCell className="text-sm">{l.lesson_title || l.lesson_id}</TableCell>
              <TableCell className="text-sm">{l.quiz_title || l.quiz_id}</TableCell>
              <TableCell><Badge variant="outline">{l.required_watch_percent}%</Badge></TableCell>
              <TableCell>{l.is_mandatory ? <Badge className="bg-red-100 text-red-700">Mandatory</Badge> : <Badge variant="outline">Optional</Badge>}</TableCell>
              <TableCell><Button size="sm" variant="ghost" className="text-red-500" onClick={() => del.mutate(l.id)}><Trash2 className="h-3 w-3" /></Button></TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table></CardContent></Card>
      <Dialog open={show} onOpenChange={setShow}>
        <DialogContent>
          <DialogHeader><DialogTitle>Link Pop-Quiz to Lesson</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Lesson (recorded)</Label>
              <Select value={form.lesson_id} onValueChange={v => setForm({ ...form, lesson_id: v })}>
                <SelectTrigger data-testid="pq-lesson-select"><SelectValue placeholder="Choose lesson…" /></SelectTrigger>
                <SelectContent>{lessons.slice(0, 100).map(l => <SelectItem key={l.id} value={l.id}>{l.title}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Quiz</Label>
              <Select value={form.quiz_id} onValueChange={v => setForm({ ...form, quiz_id: v })}>
                <SelectTrigger data-testid="pq-quiz-select"><SelectValue placeholder="Choose quiz…" /></SelectTrigger>
                <SelectContent>{quizzes.slice(0, 100).map(q => <SelectItem key={q.id} value={q.id}>{q.title}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Required Watch %</Label><Input type="number" value={form.required_watch_percent} onChange={e => setForm({ ...form, required_watch_percent: Number(e.target.value) })} /></div>
            <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={form.is_mandatory} onChange={e => setForm({ ...form, is_mandatory: e.target.checked })} />Mandatory for credit</label>
          </div>
          <DialogFooter><DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose><Button onClick={() => mut.mutate()} disabled={!form.lesson_id || !form.quiz_id || mut.isPending} data-testid="save-pop-quiz-link">{mut.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin mr-2" />}Link</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ==================== PHASE A — WEEKLY RECAP TAB ====================
const WeeklyRecapTab = ({ tenantId, subjects, classes }) => {
  const qc = useQueryClient();
  const [form, setForm] = useState({ subject_id: '', class_id: '', question_count: 10, week_start: '' });
  const { data: quizzes = [] } = useQuery({ queryKey: ['weekly-quizzes', tenantId], queryFn: () => api(`/tenants/${tenantId}/quizzes`) });
  const recaps = (quizzes || []).filter(q => q.is_weekly_recap);
  const mut = useMutation({
    mutationFn: () => api(`/tenants/${tenantId}/lms/weekly-quiz/generate`, { method: 'POST', body: JSON.stringify(form) }),
    onSuccess: (r) => { toast.success(`Generated "${r.title}" with ${r.question_count} questions from ${r.source_lessons} lessons`); qc.invalidateQueries({ queryKey: ['weekly-quizzes'] }); },
    onError: e => toast.error(e.message),
  });
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="weekly-recap-tab">
      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><Sparkles className="h-4 w-4 text-amber-500" />Generate Weekly Recap</CardTitle><CardDescription>Auto-pulls questions from last week's lessons via pop-quiz links.</CardDescription></CardHeader>
        <CardContent className="space-y-3">
          <div><Label className="text-xs">Subject (optional)</Label>
            <Select value={form.subject_id || 'all'} onValueChange={v => setForm({ ...form, subject_id: v === 'all' ? '' : v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">All subjects</SelectItem>{subjects.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs">Class (optional)</Label>
            <Select value={form.class_id || 'all'} onValueChange={v => setForm({ ...form, class_id: v === 'all' ? '' : v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">All classes</SelectItem>{classes.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs">Week Start (Mon, YYYY-MM-DD)</Label><Input type="date" value={form.week_start} onChange={e => setForm({ ...form, week_start: e.target.value })} placeholder="Defaults to last week" data-testid="weekly-week-start" /></div>
          <div><Label className="text-xs">Questions</Label><Input type="number" value={form.question_count} onChange={e => setForm({ ...form, question_count: Number(e.target.value) })} /></div>
          <Button onClick={() => mut.mutate()} disabled={mut.isPending} data-testid="generate-weekly-btn">{mut.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-2" /> : <Sparkles className="h-3.5 w-3.5 mr-1" />}Generate Recap Quiz</Button>
        </CardContent>
      </Card>
      <Card>
        <CardHeader><CardTitle className="text-base">Past recap quizzes</CardTitle></CardHeader>
        <CardContent className="space-y-2 max-h-[360px] overflow-y-auto">
          {recaps.length === 0 && <p className="text-center text-slate-400 text-sm py-6">No recaps yet.</p>}
          {recaps.map(q => (
            <div key={q.id} className="border rounded p-2 text-xs" data-testid={`recap-${q.id}`}>
              <p className="font-medium">{q.title}</p>
              <p className="text-slate-400 text-[10px]">{q.questions?.length || 0} questions · week of {(q.week_start || '').slice(0, 10)}</p>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
};

// ==================== PHASE A — LECTURER DASHBOARD TAB ====================
const LecturerDashboardTab = ({ tenantId, teachers }) => {
  const [teacherId, setTeacherId] = useState('');
  const { data, isFetching } = useQuery({
    queryKey: ['lecturer-dashboard', tenantId, teacherId],
    queryFn: () => api(`/tenants/${tenantId}/lms/lecturer-dashboard/${teacherId}`),
    enabled: !!teacherId,
  });
  return (
    <div data-testid="lecturer-dashboard-tab">
      <Card className="mb-3">
        <CardContent className="p-4 flex items-center gap-3">
          <Label className="text-xs">Select Lecturer</Label>
          <Select value={teacherId} onValueChange={setTeacherId}>
            <SelectTrigger className="w-full max-w-[260px]" data-testid="lecturer-select"><SelectValue placeholder="Pick a teacher…" /></SelectTrigger>
            <SelectContent>{(teachers || []).map(t => <SelectItem key={t.id} value={t.id}>{t.first_name || t.name} {t.last_name || ''}</SelectItem>)}</SelectContent>
          </Select>
          {isFetching && <Loader2 className="h-4 w-4 animate-spin" />}
        </CardContent>
      </Card>
      {!teacherId && <p className="text-center text-slate-400 py-10 text-sm">Pick a lecturer to see their dashboard.</p>}
      {data && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="md:col-span-1"><CardHeader className="pb-2"><CardTitle className="text-sm">Upcoming sessions ({data.upcoming_count})</CardTitle></CardHeader>
            <CardContent className="space-y-2 max-h-[300px] overflow-y-auto">
              {data.upcoming.length === 0 && <p className="text-xs text-slate-400 text-center py-4">None scheduled.</p>}
              {data.upcoming.map(u => (
                <div key={u.id} className="border rounded p-2 text-xs" data-testid={`upcoming-${u.id}`}>
                  <p className="font-medium">{u.title}</p>
                  <p className="text-slate-400 text-[10px]">{u.scheduled_at?.slice(0, 16).replace('T', ' ')} · {u.duration_minutes} min · {u.platform}</p>
                  {u.meeting_url && <a href={u.meeting_url} target="_blank" rel="noreferrer" className="text-[10px] text-indigo-600 underline">Join link</a>}
                </div>
              ))}
            </CardContent>
          </Card>
          <Card className="md:col-span-1"><CardHeader className="pb-2"><CardTitle className="text-sm">Recent sessions</CardTitle></CardHeader>
            <CardContent className="space-y-2 max-h-[300px] overflow-y-auto">
              {data.recent.length === 0 && <p className="text-xs text-slate-400 text-center py-4">No recent sessions.</p>}
              {data.recent.map(r => (
                <div key={r.session_id} className="border rounded p-2 text-xs flex justify-between items-center" data-testid={`recent-${r.session_id}`}>
                  <div>
                    <p className="font-medium">{r.title}</p>
                    <p className="text-slate-400 text-[10px]">{r.date}</p>
                    <p className="text-[10px]">Present: <strong className="text-emerald-600">{r.present}</strong> · Absent: <strong className="text-red-600">{r.absent}</strong></p>
                  </div>
                  <Badge className={r.engagement > 70 ? 'bg-emerald-100 text-emerald-700' : r.engagement > 40 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}>{r.engagement}%</Badge>
                </div>
              ))}
            </CardContent>
          </Card>
          <Card className="md:col-span-1"><CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-1"><AlertTriangle className="h-3.5 w-3.5 text-amber-500" />Students at risk</CardTitle></CardHeader>
            <CardContent className="space-y-2 max-h-[300px] overflow-y-auto">
              {data.students_at_risk.length === 0 && <p className="text-xs text-slate-400 text-center py-4">No students flagged.</p>}
              {data.students_at_risk.map(s => (
                <div key={s.student_id} className="border rounded p-2 text-xs" data-testid={`risk-${s.student_id}`}>
                  <p className="font-medium">{s.name}</p>
                  <p className="text-[10px] text-slate-500">Att: {s.avg_attended}% · Engage: {s.avg_engagement}% · Absents: {s.absents}</p>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

// ==================== PHASE A — LIVE SETTINGS TAB ====================
const LiveSettingsTab = ({ tenantId }) => {
  const qc = useQueryClient();
  const { data: settings } = useQuery({ queryKey: ['live-settings', tenantId], queryFn: () => api(`/tenants/${tenantId}/lms/live-settings`) });
  const [form, setForm] = useState({});
  useEffect(() => { if (settings) setForm(settings); }, [settings]);
  const mut = useMutation({
    mutationFn: () => api(`/tenants/${tenantId}/lms/live-settings`, { method: 'PUT', body: JSON.stringify(form) }),
    onSuccess: () => { toast.success('Settings saved'); qc.invalidateQueries({ queryKey: ['live-settings'] }); },
    onError: e => toast.error(e.message),
  });
  return (
    <Card data-testid="live-settings-tab">
      <CardHeader><CardTitle className="text-base flex items-center gap-2"><Settings2 className="h-4 w-4" />Live Class Intelligence Settings</CardTitle><CardDescription className="text-xs">Rules that govern attendance, engagement scoring, and automated reports.</CardDescription></CardHeader>
      <CardContent className="space-y-3 max-w-xl">
        <div><Label className="text-xs">Minimum attendance % to be marked Present</Label><Input type="number" value={form.min_attendance_percent ?? 75} onChange={e => setForm({ ...form, min_attendance_percent: Number(e.target.value) })} data-testid="min-attendance-input" /></div>
        <div><Label className="text-xs">Required watch % before pop-quiz unlocks</Label><Input type="number" value={form.pop_quiz_required_watch_percent ?? 80} onChange={e => setForm({ ...form, pop_quiz_required_watch_percent: Number(e.target.value) })} /></div>
        <div className="flex flex-wrap gap-4 pt-2">
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.require_camera || false} onChange={e => setForm({ ...form, require_camera: e.target.checked })} data-testid="require-camera" />Require camera on for credit</label>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.require_mic || false} onChange={e => setForm({ ...form, require_mic: e.target.checked })} />Require mic participation</label>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.auto_send_reports !== false} onChange={e => setForm({ ...form, auto_send_reports: e.target.checked })} data-testid="auto-send-reports" />Auto-send post-session reports</label>
        </div>
        <Button onClick={() => mut.mutate()} disabled={mut.isPending} data-testid="save-live-settings">{mut.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin mr-2" />}Save settings</Button>
      </CardContent>
    </Card>
  );
};

// ==================== PHASE A — FINALIZE ATTENDANCE BUTTON ====================
export const FinalizeAttendanceButton = ({ tenantId, sessionId, onDone }) => {
  const mut = useMutation({
    mutationFn: () => api(`/tenants/${tenantId}/live-classes/${sessionId}/finalize-attendance`, { method: 'POST' }),
    onSuccess: (r) => { toast.success(`Finalized · ${r.present}/${r.expected} present · ${r.reports_sent} reports sent`); onDone?.(); },
    onError: e => toast.error(e.message),
  });
  return <Button size="sm" variant="outline" onClick={() => mut.mutate()} disabled={mut.isPending} data-testid={`finalize-attendance-${sessionId}`}>{mut.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <UserCheck className="h-3.5 w-3.5 mr-1" />}Finalize &amp; Notify</Button>;
};


export default SchoolLMS;

// ==================== PHASE C — LESSON PLANS TAB ====================
// Components moved to LMSPhaseCPanels.jsx — see top-of-file import.

// ==================== Readiness checklist wrappers ====================
// Tiny shells that pull /workflow/.../readiness and drive WorkflowChecklist.
// Adding a new asset (live class / lesson) is a copy of one of these.

function AssignmentReadinessChecklist({ tenantId, assignmentId }) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['assignment-readiness', tenantId, assignmentId],
    queryFn: () => api(`/workflow/assignment/${tenantId}/${assignmentId}/readiness`),
    enabled: !!tenantId && !!assignmentId,
    refetchOnWindowFocus: true,
  });
  const publish = useMutation({
    mutationFn: () => api(`/workflow/assignment/${tenantId}/${assignmentId}/publish`, { method: 'POST' }),
    onSuccess: () => {
      toast.success('Assignment published — visible to students.');
      qc.invalidateQueries({ queryKey: ['assignment-readiness'] });
      qc.invalidateQueries({ queryKey: ['school-assignments'] });
      qc.invalidateQueries({ queryKey: ['school-assignment-detail'] });
    },
    onError: (e) => {
      const det = e?.detail || e?.message;
      if (typeof det === 'object' && det?.blockers?.length) toast.error(det.blockers[0]);
      else toast.error(typeof det === 'string' ? det : 'Cannot publish yet');
    },
  });
  return (
    <WorkflowChecklist
      data={data}
      loading={isLoading}
      onPublish={() => publish.mutate()}
      publishing={publish.isPending}
      publishLabel="Publish assignment"
    />
  );
}

function QuizReadinessChecklist({ tenantId, quizId }) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['quiz-readiness', tenantId, quizId],
    queryFn: () => api(`/workflow/quiz/${tenantId}/${quizId}/readiness`),
    enabled: !!tenantId && !!quizId,
    refetchOnWindowFocus: true,
  });
  const publish = useMutation({
    mutationFn: () => api(`/workflow/quiz/${tenantId}/${quizId}/publish`, { method: 'POST' }),
    onSuccess: () => {
      toast.success('Quiz published — students can take it now.');
      qc.invalidateQueries({ queryKey: ['quiz-readiness'] });
      qc.invalidateQueries({ queryKey: ['school-quizzes'] });
    },
    onError: (e) => {
      const det = e?.detail || e?.message;
      if (typeof det === 'object' && det?.blockers?.length) toast.error(det.blockers[0]);
      else toast.error(typeof det === 'string' ? det : 'Cannot publish yet');
    },
  });
  return (
    <WorkflowChecklist
      data={data}
      loading={isLoading}
      onPublish={() => publish.mutate()}
      publishing={publish.isPending}
      publishLabel="Publish quiz"
    />
  );
}
